import { useState, useEffect } from 'react'
import { X, Download, Copy, Check, Pencil, Save, Clock, Tag, FileText } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import rehypeHighlight from 'rehype-highlight'
import { api, KbDocDetail, KbCategory } from '../../lib/api'

interface Props {
  documentId: string
  categories: KbCategory[]
  onClose: () => void
  onUpdated: () => void
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  })
}

function sourceLabel(type: string) {
  switch (type) {
    case 'upload': return 'Uploaded file'
    case 'paste': return 'Pasted content'
    case 'solution_output': return 'From Solutions Builder'
    default: return type
  }
}

export function KbDocumentViewer({ documentId, categories, onClose, onUpdated }: Props) {
  const [doc, setDoc] = useState<KbDocDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [copied, setCopied] = useState(false)
  const [saving, setSaving] = useState(false)

  // Edit state
  const [editTitle, setEditTitle] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editCategoryId, setEditCategoryId] = useState('')
  const [editTags, setEditTags] = useState('')
  const [editContent, setEditContent] = useState('')

  useEffect(() => {
    loadDocument()
  }, [documentId])

  async function loadDocument() {
    setLoading(true)
    try {
      const d = await api.solutions.kb.get(documentId)
      setDoc(d)
      setEditTitle(d.title)
      setEditDescription(d.description || '')
      setEditCategoryId(d.category_id || '')
      setEditTags(d.tags.join(', '))
      setEditContent(d.content_md || '')
    } catch (err) {
      console.error('Failed to load document:', err)
    } finally {
      setLoading(false)
    }
  }

  async function handleCopy() {
    if (!doc?.content_md) return
    await navigator.clipboard.writeText(doc.content_md)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  async function handleDownload() {
    if (!doc) return
    try {
      const blob = await api.solutions.kb.download(doc.id)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = doc.filename || `${doc.title}.md`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Download failed:', err)
    }
  }

  async function handleSave() {
    if (!doc) return
    setSaving(true)
    try {
      const updated = await api.solutions.kb.update(doc.id, {
        title: editTitle,
        description: editDescription || undefined,
        contentMd: editContent || undefined,
        categoryId: editCategoryId || null,
        tags: editTags ? editTags.split(',').map(t => t.trim()).filter(Boolean) : [],
      })
      setDoc(updated)
      setEditing(false)
      onUpdated()
    } catch (err) {
      console.error('Save failed:', err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-xl w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex-1 min-w-0">
            {editing ? (
              <input
                type="text"
                value={editTitle}
                onChange={e => setEditTitle(e.target.value)}
                className="w-full text-base font-semibold text-gray-900 border border-gray-300 rounded-lg px-3 py-1.5 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
              />
            ) : (
              <h2 className="text-base font-semibold text-gray-900 truncate">{doc?.title || 'Loading...'}</h2>
            )}
          </div>
          <div className="flex items-center gap-1 ml-3 flex-shrink-0">
            {editing ? (
              <>
                <button
                  onClick={() => setEditing(false)}
                  className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 rounded-lg transition-colors"
                >
                  <Save className="w-3 h-3" />
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setEditing(true)}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                  title="Edit"
                >
                  <Pencil className="w-4 h-4" />
                </button>
                <button
                  onClick={handleCopy}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                  title="Copy content"
                >
                  {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </button>
                <button
                  onClick={handleDownload}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                  title="Download"
                >
                  <Download className="w-4 h-4" />
                </button>
              </>
            )}
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded transition-colors ml-1">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Metadata bar */}
        {doc && !loading && (
          <div className="px-5 py-3 border-b border-gray-100 bg-gray-50 flex-shrink-0">
            <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
              {/* Category */}
              {editing ? (
                <select
                  value={editCategoryId}
                  onChange={e => setEditCategoryId(e.target.value)}
                  className="px-2 py-1 text-xs border border-gray-300 rounded bg-white focus:border-indigo-500 outline-none"
                >
                  <option value="">No category</option>
                  {categories.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              ) : doc.category_name ? (
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-white"
                  style={{ backgroundColor: doc.category_color || '#6366f1' }}
                >
                  {doc.category_name}
                </span>
              ) : null}

              <span className="flex items-center gap-1">
                <FileText className="w-3 h-3" />
                {sourceLabel(doc.source_type)}
              </span>

              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Updated {formatDate(doc.updated_at)}
              </span>

              {doc.file_size_bytes && (
                <span>{(doc.file_size_bytes / 1024).toFixed(1)} KB</span>
              )}
            </div>

            {/* Tags */}
            {editing ? (
              <div className="mt-2">
                <div className="flex items-center gap-1">
                  <Tag className="w-3 h-3 text-gray-400" />
                  <input
                    type="text"
                    value={editTags}
                    onChange={e => setEditTags(e.target.value)}
                    placeholder="tag1, tag2, ..."
                    className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded bg-white focus:border-indigo-500 outline-none"
                  />
                </div>
              </div>
            ) : doc.tags.length > 0 ? (
              <div className="flex items-center gap-1 mt-2">
                <Tag className="w-3 h-3 text-gray-400" />
                {doc.tags.map(tag => (
                  <span key={tag} className="px-1.5 py-0.5 bg-white border border-gray-200 text-gray-600 rounded text-[10px]">
                    {tag}
                  </span>
                ))}
              </div>
            ) : null}

            {/* Description */}
            {editing ? (
              <div className="mt-2">
                <input
                  type="text"
                  value={editDescription}
                  onChange={e => setEditDescription(e.target.value)}
                  placeholder="Description (optional)"
                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded bg-white focus:border-indigo-500 outline-none"
                />
              </div>
            ) : doc.description ? (
              <p className="text-xs text-gray-500 mt-1">{doc.description}</p>
            ) : null}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center h-32 text-gray-400">
              <p className="text-sm">Loading document...</p>
            </div>
          ) : editing ? (
            <textarea
              value={editContent}
              onChange={e => setEditContent(e.target.value)}
              className="w-full h-full min-h-[300px] px-3 py-2 text-sm border border-gray-300 rounded-lg font-mono resize-y focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 outline-none"
            />
          ) : doc?.content_md ? (
            <div className="prose prose-sm max-w-none">
              <ReactMarkdown rehypePlugins={[rehypeHighlight]}>
                {doc.content_md}
              </ReactMarkdown>
            </div>
          ) : (
            <div className="flex items-center justify-center h-32 text-gray-400">
              <p className="text-sm">No text content available for this document</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
